import { useCallback, useEffect, useId, useRef } from 'react'
import type { KeyboardEvent, MouseEvent, ReactNode, SyntheticEvent } from 'react'
import './Dialog.css'

export interface DialogProps {
  /** Visible heading; it also names the dialog for screen readers. */
  title: ReactNode
  /** Optional line under the heading, announced as the dialog's description. */
  description?: ReactNode
  /** Called by Escape and the close button. The parent closes the dialog by unmounting it. */
  onClose: () => void
  /** While true (e.g. a save in flight), Escape and the close button do nothing. */
  closeDisabled?: boolean
  size?: 'default' | 'large'
  /** 'sheet' rises from the bottom of a phone (menus, lists) and, holding no form, also closes on a backdrop tap. */
  placement?: 'center' | 'sheet'
  className?: string
  children: ReactNode
}

const FOCUSABLE =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'

/**
 * A modal pop-up on the native <dialog>, opened with showModal(): the browser supplies the modal role, keeps Tab
 * inside and makes the page behind it inert. Render it to open it and unmount it to close it. Focus starts on the
 * first control in the content (falling back to the close button) and goes back to whatever opened it.
 *
 * A click on the backdrop deliberately does nothing: these dialogs hold forms, and a stray click outside must not
 * throw away what someone typed. Escape, the close button or the content's own Cancel close it. A sheet holds no
 * form, so there a backdrop tap closes it too.
 */
export function Dialog({
  title,
  description,
  onClose,
  closeDisabled = false,
  size = 'default',
  placement = 'center',
  className,
  children,
}: DialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const closeRef = useRef<HTMLButtonElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)

  const openAsModal = useCallback((dialog: HTMLDialogElement | null) => {
    if (!dialog || dialog.open) return
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    dialog.showModal()
    const first = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].find((el) => el !== closeRef.current)
    ;(first ?? closeRef.current)?.focus()
  }, [])
  useEffect(() => () => openerRef.current?.focus(), [])

  const requestClose = () => {
    if (!closeDisabled) onClose()
  }

  // Escape fires 'cancel'; stop the browser closing the dialog itself, since the parent unmounts it.
  const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault()
    requestClose()
  }

  // Also handled on keydown (which stops the browser's own cancel), so jsdom, which has no 'cancel', behaves the
  // same. Content that handles Escape itself can preventDefault to keep the dialog open.
  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return
    event.preventDefault()
    requestClose()
  }

  // A tap on the ::backdrop lands on the <dialog> itself; the visible sheet is its content.
  const handleBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) requestClose()
  }

  const sheet = placement === 'sheet'
  const classes = ['dialog', size === 'large' && 'dialog--large', sheet && 'dialog--sheet', className].filter(Boolean).join(' ')

  return (
    <dialog
      ref={openAsModal}
      className={classes}
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={handleCancel}
      onKeyDown={handleKeyDown}
      onClick={sheet ? handleBackdropClick : undefined}
    >
      {sheet ? <span className="dialog__grab" aria-hidden="true" /> : null}
      <header className="dialog__header">
        <h2 id={titleId} className="dialog__title">
          {title}
        </h2>
        <button
          ref={closeRef}
          type="button"
          className="dialog__close"
          aria-label="Close"
          onClick={requestClose}
          disabled={closeDisabled}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>
      {description ? (
        <p id={descriptionId} className="dialog__description">
          {description}
        </p>
      ) : null}
      {children}
    </dialog>
  )
}
